import React from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import DOMPurify from 'dompurify';
import { ArrowLeft, CheckCircle2, Lock, Share2 } from 'lucide-react';
import { Card, Button } from '../components';
import { geefToestemming, getMijnVerslagen } from '../services/gespreksverslagApi';

/**
 * Medewerker-portaal — gespreksverslagen (migratie 176).
 * Vak A (persoonlijke reflectie & huiswerk) is alleen hier te lezen; de
 * werkgever ziet er nooit iets van. Een management-update (Deel 5) gaat pas
 * naar HR als de medewerker hier toestemming geeft.
 */
const MedewerkerVerslagenPage: React.FC = () => {
  const qc = useQueryClient();
  const { data: verslagen = [], isLoading } = useQuery({ queryKey: ['mijn-verslagen'], queryFn: getMijnVerslagen });
  const toestemming = useMutation({
    mutationFn: geefToestemming,
    onSuccess: () => {
      toast.success('Toestemming gegeven — HR ziet nu alleen de management-update');
      qc.invalidateQueries({ queryKey: ['mijn-verslagen'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Er ging iets mis'),
  });

  return (
    <div className="space-y-5">
      <Link to="/medewerker" className="inline-flex items-center text-sm text-[#0B1F3A]"><ArrowLeft className="w-4 h-4 mr-1" />Terug</Link>
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Mijn gespreksverslagen</h1>
        <p className="flex gap-1.5 text-sm text-gray-600 mt-1">
          <Lock className="w-4 h-4 mt-0.5 shrink-0" aria-hidden />
          Je persoonlijke notities zijn alleen voor jou. Je werkgever en HR kunnen ze nooit inzien.
        </p>
      </div>

      {isLoading && <p className="text-sm text-gray-500">Laden…</p>}
      {!isLoading && verslagen.length === 0 && <p className="text-sm text-gray-500">Er zijn nog geen verslagen.</p>}

      <ul className="space-y-3">
        {verslagen.map((v) => (
          <li key={v.id}>
            <Card className="p-4 space-y-3">
              <p className="text-xs text-gray-500">
                {new Date(v.datum).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })} · {v.gesprek} · {v.professional}
              </p>
              {v.persoonlijke_notities ? (
                <div>
                  <h2 className="text-sm font-semibold text-gray-900 mb-1">Persoonlijke reflectie & huiswerk</h2>
                  <div className="prose prose-sm max-w-none text-gray-800" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(v.persoonlijke_notities) }} />
                </div>
              ) : (
                <p className="text-sm text-gray-500 italic">Geen persoonlijke notities bij dit gesprek.</p>
              )}
              {v.deel5 && (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-2 text-sm">
                  <h2 className="font-semibold text-gray-900">Management-update voor HR</h2>
                  <p className="text-xs text-gray-600">Dit is precies wat HR te zien krijgt, en alleen als jij toestemming geeft.</p>
                  <p><span className="font-medium">Arbo-kader:</span> {v.deel5.arbo_kader}</p>
                  {v.deel5.fte_belastbaarheid != null && (
                    <p><span className="font-medium">Belastbaarheid:</span> {v.deel5.fte_belastbaarheid}% · <span className="font-medium">Verwachte verzuimduur:</span> {v.deel5.verwachte_verzuimreductie}</p>
                  )}
                  <p className="whitespace-pre-wrap">{v.deel5.werkvloer_advies}</p>
                  {v.toestemming === 'gegeven' && (
                    <p className="flex items-center gap-1 text-green-700"><CheckCircle2 className="w-4 h-4" aria-hidden />Gedeeld met HR</p>
                  )}
                  {v.toestemming === 'verlopen' && <p className="text-gray-500">Het verzoek is verlopen; er is niets gedeeld.</p>}
                  {v.toestemming === 'gevraagd' && (
                    <Button size="sm" loading={toestemming.isPending} onClick={() => toestemming.mutate(v.id)}>
                      <Share2 className="w-4 h-4 mr-1" />Ik geef toestemming om dit met HR te delen
                    </Button>
                  )}
                </div>
              )}
            </Card>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default MedewerkerVerslagenPage;
